import * as React from 'react';
import { ArrowDown, ArrowUp, CalendarBlank, CaretDown } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { parseIsoDate, toIsoDate } from '@/lib/utils/custom-date/custom-date';
import { Input } from '@/components/ui-kit/input';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui-kit/popover';
import { Calendar } from '@/components/ui-kit/calendar';

const toDisplayValue = (date?: Date): string => {
  if (!date) return '';
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${m}/${d}/${date.getFullYear()}`;
};

const formatRawDigits = (raw: string): string => {
  if (raw.length > 4) return `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4)}`;
  if (raw.length > 2) return `${raw.slice(0, 2)}/${raw.slice(2)}`;
  return raw;
};

// The year dropdown lists this many years around today unless `min` / `max` bound it (react-day-picker's
// own default stops at the current year, so no future date could be picked). A field for dates further
// back (birth, joining, work history) widens it with `yearsBack`.
const DEFAULT_YEAR_SPAN = 5;

interface DatePickerProps {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  /** Years before today the year dropdown starts at when there is no `min`. */
  yearsBack?: number;
  /** Years after today the year dropdown ends at when there is no `max`. */
  yearsAhead?: number;
  className?: string;
  id?: string;
  name?: string;
  'aria-label'?: string;
}

export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  {
    value,
    onChange,
    onBlur,
    placeholder = 'mm/dd/yyyy',
    disabled,
    min,
    max,
    yearsBack = DEFAULT_YEAR_SPAN,
    yearsAhead = DEFAULT_YEAR_SPAN,
    className,
    id,
    name,
    'aria-label': ariaLabel,
  },
  ref
) {
  const { t } = useTranslation();
  const selectedDate = React.useMemo(() => parseIsoDate(value), [value]);
  const minDate = React.useMemo(() => parseIsoDate(min), [min]);
  const maxDate = React.useMemo(() => parseIsoDate(max), [max]);
  // A saved date outside the default span still gets its year listed.
  const [startMonth, endMonth] = React.useMemo(() => {
    const year = new Date().getFullYear();
    const start = minDate ?? new Date(year - yearsBack, 0, 1);
    const end = maxDate ?? new Date(year + yearsAhead, 11, 1);
    if (!selectedDate) return [start, end];
    return [
      !minDate && selectedDate < start ? selectedDate : start,
      !maxDate && selectedDate > end ? selectedDate : end,
    ];
  }, [minDate, maxDate, selectedDate, yearsBack, yearsAhead]);

  const [inputValue, setInputValue] = React.useState(() => toDisplayValue(selectedDate));
  const [month, setMonth] = React.useState<Date>(selectedDate ?? new Date());
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    setInputValue(toDisplayValue(selectedDate));
    if (selectedDate) setMonth(selectedDate);
  }, [selectedDate]);

  const isWithinRange = (date: Date) =>
    !(minDate && date < minDate) && !(maxDate && date > maxDate);

  const commitDate = (date: Date | undefined) => {
    if (!date || !isWithinRange(date)) return;
    onChange(toIsoDate(date));
    setMonth(date);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 8);
    setInputValue(formatRawDigits(raw));

    if (raw.length === 0) {
      onChange('');
      return;
    }
    if (raw.length !== 8) return;

    const mm = Number(raw.slice(0, 2));
    const dd = Number(raw.slice(2, 4));
    const yyyy = Number(raw.slice(4, 8));
    const date = new Date(yyyy, mm - 1, dd);
    if (date.getFullYear() === yyyy && date.getMonth() === mm - 1 && date.getDate() === dd) {
      commitDate(date);
    }
  };

  const handleSelect = (date: Date | undefined) => {
    commitDate(date);
    setOpen(false);
  };

  const handleToday = () => {
    const now = new Date();
    commitDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
    setOpen(false);
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
    setOpen(false);
  };

  const handleInputBlur = () => {
    // Reverts an incomplete or out-of-range typed value back to the last committed date,
    // so the field never gets stuck showing text that doesn't match its actual value.
    setInputValue(toDisplayValue(selectedDate));
    onBlur?.();
  };

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverAnchor asChild>
        {/* Component Map › Date fields: as wide as a date (168px), not across the whole field; a caller's
            own width wins. */}
        <div className={cn('relative w-[168px] max-w-full', className)}>
          <Input
            ref={ref}
            id={id}
            name={name}
            aria-label={ariaLabel}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            disabled={disabled}
            inputMode="numeric"
            autoComplete="off"
            className="pr-9"
          />
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={t('OPEN_CALENDAR')}
              className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-muted-foreground transition-colors duration-fast ease-standard hover:text-foreground disabled:cursor-not-allowed disabled:text-disabled-foreground disabled:hover:text-disabled-foreground"
            >
              <CalendarBlank className="size-4" />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown"
          buttonVariant="ghost"
          month={month}
          onMonthChange={setMonth}
          selected={selectedDate}
          onSelect={handleSelect}
          startMonth={startMonth}
          endMonth={endMonth}
          disabled={(date) => !isWithinRange(date)}
          formatters={{
            formatMonthDropdown: (date) => date.toLocaleString('default', { month: 'long' }),
          }}
          components={{
            Chevron: ({ className: chevronClassName, orientation, ...props }) => {
              if (orientation === 'left') {
                return <ArrowUp className={cn('h-4 w-4', chevronClassName)} {...props} />;
              }
              if (orientation === 'right') {
                return <ArrowDown className={cn('h-4 w-4', chevronClassName)} {...props} />;
              }
              return <CaretDown className={cn('h-4 w-4', chevronClassName)} {...props} />;
            },
          }}
          classNames={{
            month_caption: 'flex h-9 items-center justify-start pl-2 pr-24 text-sm font-semibold',
            nav: 'absolute inset-x-0 top-0 flex h-9 items-center justify-end gap-0.5 pr-1',
          }}
        />
        <div className="flex items-center justify-between border-t px-3 py-2">
          <button
            type="button"
            onClick={handleClear}
            className="text-sm font-medium text-primary hover:underline"
          >
            {t('CLEAR')}
          </button>
          <button
            type="button"
            onClick={handleToday}
            className="text-sm font-medium text-primary hover:underline"
          >
            {t('TODAY')}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
});
